import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Button } from '@/components/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuthStore, hasPermission, hasTenantWidePermission } from '@/stores/auth';
import { useLabel } from '@/lib/labels';

/**
 * `/leave/absences` (v0.4 休暇管理 TD-26 段階⑥、§6-1 L-4)。産休・育休・介護休業の期間計算・確定・
 * 分割・延長・就業禁止の解除 (H-5) を扱う。
 *
 * ⚠️ §6-3 の必須要件: ③付与日数ではなく期間の根拠を必ず併記 (basis/citations) / ④産後8週の就業可否
 * には責任分界表示 / ⑨育児休業給付金等の案内文はリンクのみ (金額計算・申請期限の提示はしない)。
 * ⚠️ H-5: 就業禁止の解除は「本人の請求」ボタン (自分の休業でのみ表示) と「医師の許可の記録」ボタン
 * (leave_grant:manage:all_stores を持つ人のみ表示) の2つに分離する。同じボタンを共有しない。
 * 設計: docs/design/v04-leave-management-detail.md §5-4 / §6-1 L-4 / §6-3
 */

const STATUTORY_KIND_LABELS: Record<string, string> = {
  maternity_prenatal: '産前休業', maternity_postnatal: '産後休業',
  childcare: '育児休業', childcare_at_birth: '出生時育児休業 (産後パパ育休)', family_care: '介護休業',
};

type LeaveTypeOption = {
  leave_type_id: string; label: string; statutory_kind: string | null; is_period_type: boolean; requires_family_target: boolean;
};

type ComputeResult = {
  proposed_start_date: string; proposed_end_date: string | null;
  basis: Record<string, unknown>; citations: string[]; warnings: string[];
};

type WorkRelease = {
  released: boolean; requested_at: string | null; physician_approval_recorded_at: string | null; effective_from: string | null;
} | null;

type AbsenceRow = {
  absence_id: string; employee_id: string; leave_type_id: string; family_member_id: string | null; sequence_no: number;
  planned_start_date: string | null; planned_end_date: string | null; actual_start_date: string | null; actual_end_date: string | null;
  status: string; work_prohibition: string; basis: Record<string, unknown> | null;
  row_version: number; work_release: WorkRelease;
};

const STATUS_LABELS: Record<string, string> = { planned: '予定', active: '休業中', ended: '終了', cancelled: '取消' };

type Employee = { employee_id: string; employee_number: string; family_name: string; given_name: string; status: string };

/** §6-3③ の「根拠を必ず併記する」を満たすため、`basis` (制度ごとに形が異なる JSON) を
 *  raw dump ではなく人が読める日本語の箇条書きに変換する (design-expert HIGH 指摘の反映)。
 *  未知のキーが混ざっても (将来 absence-period.ts が項目を追加しても) 落ちないよう、
 *  ラベル表に無いキーは「キー名: 値」のまま表示するフォールバックを持つ。 */
const BASIS_FIELD_LABELS: Record<string, string> = {
  anchor_date: '起算日', weeks: '週数', is_multiple_pregnancy: '多胎妊娠',
  is_request_based: '本人の請求による休業', absolute_weeks: '絶対的就業禁止の週数', total_weeks: '産後休業の総週数',
  absolute_until: '絶対的就業禁止の終期', cap_end_date: '法定上限日', extended: '延長 (最長2歳)',
  split_count_limit: '分割回数の上限', window_end: '出生後の窓の終期', max_days: '通算上限日数',
  prior_days_used_in_window: '窓内の既取得日数', remaining_days: '残日数', days_per_target: '対象家族あたりの通算上限日数',
  prior_days_used_for_target: '既取得日数', missing_legal_constant: '未確認の法定値キー',
};
const formatBasisValue = (value: unknown): string => {
  if (typeof value === 'boolean') return value ? 'はい' : 'いいえ';
  if (value === null || value === undefined) return '—';
  return String(value);
};
function BasisSummary({ basis }: { basis: Record<string, unknown> }) {
  const entries = Object.entries(basis).filter(([k]) => k !== 'anchor');
  if (entries.length === 0) return null;
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
      {entries.map(([key, value]) => (
        <div key={key} className="contents">
          <dt className="text-text-sub">{BASIS_FIELD_LABELS[key] ?? key}</dt>
          <dd className="text-text-body">{formatBasisValue(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

/** 社員を氏名・社員番号で選ぶセレクタ (design-expert HIGH 指摘の反映: 生の employee_id
 *  テキスト入力は入力ミスの温床になるため、settings/VisibilityRulesSettingsPage.tsx の
 *  EmployeeSelector と同型のパターンを踏襲する)。 */
function EmployeeSelector({ employeeId, onChange }: { employeeId: string; onChange: (id: string) => void }) {
  const { data: employees = [] } = useQuery<Employee[]>({ queryKey: ['employees'], queryFn: () => apiFetch<Employee[]>('/employees') });
  const NULL_VALUE = '__self__';
  return (
    <Select value={employeeId || NULL_VALUE} onValueChange={(v) => onChange(v === NULL_VALUE ? '' : v)}>
      <SelectTrigger className="w-full max-w-md min-h-[44px]"><SelectValue placeholder="社員を選ぶ" /></SelectTrigger>
      <SelectContent>
        <SelectItem value={NULL_VALUE}>(自分自身)</SelectItem>
        {employees.map((e) => (
          <SelectItem key={e.employee_id} value={e.employee_id}>{e.family_name} {e.given_name} ({e.employee_number})</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function useLeaveTypeOptions() {
  return useQuery<LeaveTypeOption[]>({
    queryKey: ['leave-types-for-absences'],
    queryFn: async () => {
      const rows = await apiFetch<Array<Record<string, unknown>>>('/leave-types');
      return rows
        .map((r) => ({
          leave_type_id: r.leave_type_id as string, label: r.label as string,
          statutory_kind: (r.statutory_kind as string | null) ?? null,
          is_period_type: r.is_period_type as boolean, requires_family_target: r.requires_family_target as boolean,
        }))
        .filter((r) => r.is_period_type);
    },
  });
}

function ComputeAndConfirmPanel({ employeeId, onConfirmed }: { employeeId: string; onConfirmed: () => void }) {
  const { data: leaveTypes = [] } = useLeaveTypeOptions();
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [familyMemberId, setFamilyMemberId] = useState('');
  const [expectedDueDate, setExpectedDueDate] = useState('');
  const [isMultiple, setIsMultiple] = useState(false);
  const [actualBirthDate, setActualBirthDate] = useState('');
  const [childBirthDate, setChildBirthDate] = useState('');
  const [extended, setExtended] = useState(false);
  const [requestedStart, setRequestedStart] = useState('');
  const [requestedEnd, setRequestedEnd] = useState('');
  const [result, setResult] = useState<ComputeResult | null>(null);

  const selectedType = useMemo(() => leaveTypes.find((t) => t.leave_type_id === leaveTypeId), [leaveTypes, leaveTypeId]);
  const kind = selectedType?.statutory_kind ?? null;

  const computeMut = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { employee_id: employeeId || undefined, leave_type_id: leaveTypeId, family_member_id: familyMemberId || null };
      if (kind === 'maternity_prenatal') { body.expected_due_date = expectedDueDate; body.is_multiple_pregnancy = isMultiple; }
      else if (kind === 'maternity_postnatal') { body.actual_birth_date = actualBirthDate; }
      else if (kind === 'childcare') { body.child_birth_date = childBirthDate; body.requested_start_date = requestedStart; body.requested_end_date = requestedEnd || null; body.extended = extended; }
      else { body.child_birth_date = childBirthDate; body.requested_start_date = requestedStart; body.requested_end_date = requestedEnd; }
      return apiFetch<ComputeResult>('/leave/absences/compute', { method: 'POST', body: JSON.stringify(body) });
    },
    onSuccess: (data) => setResult(data),
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '期間の計算に失敗しました'),
  });

  const qc = useQueryClient();
  const confirmMut = useMutation({
    mutationFn: () => apiFetch('/leave/absences', {
      method: 'POST',
      body: JSON.stringify({
        employee_id: employeeId || undefined, leave_type_id: leaveTypeId, family_member_id: familyMemberId || null,
        planned_start_date: result!.proposed_start_date, planned_end_date: result!.proposed_end_date,
        status: 'planned', basis: result!.basis,
      }),
    }),
    onSuccess: () => {
      toast.success('休業を確定しました');
      setResult(null);
      qc.invalidateQueries({ queryKey: ['leave-absences'] });
      onConfirmed();
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '確定に失敗しました'),
  });

  // design-and-code LOW 指摘反映: 対象家族必須の制度は family_member_id が無いとサーバーが必ず
  // 400 を返す (無駄な往復を避ける)。
  const canCompute = !!leaveTypeId && (!selectedType?.requires_family_target || !!familyMemberId) && (
    (kind === 'maternity_prenatal' && !!expectedDueDate)
    || (kind === 'maternity_postnatal' && !!actualBirthDate)
    || (kind === 'childcare' && !!childBirthDate && !!requestedStart)
    || (kind === 'childcare_at_birth' && !!childBirthDate && !!requestedStart && !!requestedEnd)
    || (kind === 'family_care' && !!requestedStart && !!requestedEnd)
  );

  return (
    <div className="border border-surface-border rounded-lg bg-white p-4 sm:p-5 space-y-3">
      <h3 className="text-sm font-bold text-text-body">休業の期間を計算する</h3>
      <div>
        <label className="block text-xs text-text-sub mb-1">制度</label>
        <Select value={leaveTypeId || undefined} onValueChange={(v) => { setLeaveTypeId(v); setResult(null); }}>
          <SelectTrigger className="min-h-[44px]"><SelectValue placeholder="制度を選ぶ" /></SelectTrigger>
          <SelectContent>
            {leaveTypes.map((t) => (
              <SelectItem key={t.leave_type_id} value={t.leave_type_id}>
                {t.label}{t.statutory_kind ? ` (${STATUTORY_KIND_LABELS[t.statutory_kind] ?? t.statutory_kind})` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedType?.requires_family_target && (
        <div>
          <label className="block text-xs text-text-sub mb-1">対象家族ID</label>
          <input type="text" value={familyMemberId} onChange={(e) => setFamilyMemberId(e.target.value)}
            placeholder="家族情報画面で確認した対象家族のID"
            className="min-h-[44px] w-full px-3 py-1.5 border border-surface-border rounded text-sm bg-white" />
        </div>
      )}

      {kind === 'maternity_prenatal' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-text-sub mb-1">出産予定日</label>
            <input type="date" value={expectedDueDate} onChange={(e) => setExpectedDueDate(e.target.value)}
              className="min-h-[44px] w-full px-3 py-1.5 border border-surface-border rounded text-sm bg-white" />
          </div>
          <label className="flex items-center gap-2 text-sm text-text-body mt-6 min-h-[44px]">
            <input type="checkbox" className="w-5 h-5" checked={isMultiple} onChange={(e) => setIsMultiple(e.target.checked)} />
            多胎妊娠
          </label>
        </div>
      )}
      {kind === 'maternity_postnatal' && (
        <div>
          <label className="block text-xs text-text-sub mb-1">実出産日</label>
          <input type="date" value={actualBirthDate} onChange={(e) => setActualBirthDate(e.target.value)}
            className="min-h-[44px] w-full px-3 py-1.5 border border-surface-border rounded text-sm bg-white" />
        </div>
      )}
      {(kind === 'childcare' || kind === 'childcare_at_birth') && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-text-sub mb-1">子の生年月日</label>
            <input type="date" value={childBirthDate} onChange={(e) => setChildBirthDate(e.target.value)}
              className="min-h-[44px] w-full px-3 py-1.5 border border-surface-border rounded text-sm bg-white" />
          </div>
          {kind === 'childcare' && (
            <label className="flex items-center gap-2 text-sm text-text-body mt-6 min-h-[44px]">
              <input type="checkbox" className="w-5 h-5" checked={extended} onChange={(e) => setExtended(e.target.checked)} />
              延長 (最長2歳まで)
            </label>
          )}
        </div>
      )}
      {(kind === 'childcare' || kind === 'childcare_at_birth' || kind === 'family_care') && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-text-sub mb-1">開始希望日</label>
            <input type="date" value={requestedStart} onChange={(e) => setRequestedStart(e.target.value)}
              className="min-h-[44px] w-full px-3 py-1.5 border border-surface-border rounded text-sm bg-white" />
          </div>
          <div>
            <label className="block text-xs text-text-sub mb-1">終了希望日{kind === 'childcare' ? ' (任意、未入力は上限まで)' : ''}</label>
            <input type="date" value={requestedEnd} onChange={(e) => setRequestedEnd(e.target.value)}
              className="min-h-[44px] w-full px-3 py-1.5 border border-surface-border rounded text-sm bg-white" />
          </div>
        </div>
      )}

      <Button variant="secondary" size="sm" disabled={!canCompute || computeMut.isPending} loading={computeMut.isPending} onClick={() => computeMut.mutate()}>
        計算する
      </Button>

      {result && (
        <div className="border-t border-surface-line pt-3 space-y-2">
          <p className="text-sm font-medium text-text-body">
            計算結果: {result.proposed_start_date} 〜 {result.proposed_end_date ?? '(未定)'}
          </p>
          {/* §6-3 必須要件③: 付与日数と同様、期間の根拠を必ず併記する */}
          <div className="text-xs bg-surface-sunken rounded p-2 space-y-1">
            <p className="font-medium text-text-body">根拠</p>
            <BasisSummary basis={result.basis} />
            {result.citations.length > 0 && <p className="text-text-sub">出典: {result.citations.join(' / ')}</p>}
          </div>
          {result.warnings.length > 0 && (
            <ul className="space-y-1">
              {result.warnings.map((w, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-alert-text">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          )}
          {/* §6-3 必須要件④: 産後の就業可否には責任分界表示 */}
          {kind === 'maternity_postnatal' && (
            <p className="text-xs text-text-sub border border-surface-border rounded p-2">
              この期間の就業可否は労働基準法第65条第2項に基づく計算結果です。医師の許可の有無・内容の妥当性は貴社にてご確認ください。
              MiseSpark は法令の適用可否を判断しません。
            </p>
          )}
          {/* §6-3 必須要件⑨: 給付金等の案内はリンクのみ (金額計算・申請期限の提示はしない) */}
          {(kind === 'maternity_prenatal' || kind === 'maternity_postnatal' || kind === 'childcare' || kind === 'childcare_at_birth') && (
            <p className="text-xs text-text-sub flex items-center gap-1">
              {kind === 'maternity_postnatal' ? '出産手当金' : '育児休業給付金'}・社会保険料免除の手続きが別途必要です。
              <a href={kind === 'maternity_postnatal' ? 'https://www.nenkin.go.jp/' : 'https://www.hellowork.mhlw.go.jp/'}
                target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-brand-blue underline">
                案内を見る<ExternalLink size={12} aria-hidden />
              </a>
            </p>
          )}
          <Button variant="primary" size="sm" disabled={confirmMut.isPending} loading={confirmMut.isPending} onClick={() => confirmMut.mutate()}>
            この内容で確定する
          </Button>
        </div>
      )}
    </div>
  );
}

function WorkReleaseSection({ absence, isSelf, canManageGrant, onChanged }: {
  absence: AbsenceRow; isSelf: boolean; canManageGrant: boolean; onChanged: () => void;
}) {
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const requestMut = useMutation({
    mutationFn: () => apiFetch(`/leave/absences/${absence.absence_id}/work-release/request`, { method: 'POST' }),
    onSuccess: () => { toast.success('請求を記録しました'); onChanged(); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '請求の記録に失敗しました'),
  });
  const physicianMut = useMutation({
    mutationFn: () => apiFetch(`/leave/absences/${absence.absence_id}/work-release/physician`, {
      method: 'POST', body: JSON.stringify({ effective_from: effectiveFrom }),
    }),
    onSuccess: () => { toast.success('医師の許可を記録しました'); setEffectiveFrom(''); onChanged(); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '記録に失敗しました'),
  });

  const wr = absence.work_release;
  return (
    <div className="border-t border-surface-line pt-2 mt-2 space-y-2">
      <p className="text-xs text-text-sub">
        就業禁止の解除 (労働基準法第65条第2項ただし書): 本人の請求 {wr?.requested_at ? '済' : '未'} / 医師の許可の記録 {wr?.physician_approval_recorded_at ? '済' : '未'}
        {wr?.released && wr.effective_from && ` (${wr.effective_from} から就業可能)`}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {isSelf && (
          <Button variant="secondary" size="sm" disabled={requestMut.isPending} loading={requestMut.isPending} onClick={() => requestMut.mutate()}>
            本人として請求する
          </Button>
        )}
        {canManageGrant && (
          <>
            <input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)}
              className="min-h-[44px] px-3 py-1.5 border border-surface-border rounded text-sm bg-white" />
            <Button variant="secondary" size="sm" disabled={!effectiveFrom || physicianMut.isPending} loading={physicianMut.isPending}
              onClick={() => physicianMut.mutate()}>
              医師の許可を記録する
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function AbsenceList({ employeeId, isSelf, canManageGrant }: { employeeId: string; isSelf: boolean; canManageGrant: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery<{ absences: AbsenceRow[] }>({
    queryKey: ['leave-absences', employeeId],
    queryFn: () => apiFetch<{ absences: AbsenceRow[] }>(`/leave/absences${employeeId ? `?employee_id=${encodeURIComponent(employeeId)}` : ''}`),
  });

  if (isLoading) return <div className="text-text-sub">読み込み中…</div>;
  if (isError) return <p className="text-sm text-alert-text">休業記録の取得に失敗しました</p>;
  const absences = data?.absences ?? [];
  if (absences.length === 0) return <p className="text-sm text-text-sub">休業記録はありません。</p>;

  const onChanged = () => { qc.invalidateQueries({ queryKey: ['leave-absences', employeeId] }); refetch(); };

  return (
    <div className="space-y-3">
      {absences.map((a) => (
        <div key={a.absence_id} className="border border-surface-line rounded-lg p-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm font-medium text-text-body">
              {(a.actual_start_date ?? a.planned_start_date) || '(未定)'} 〜 {(a.actual_end_date ?? a.planned_end_date) || '(未定)'}
              <span className="ml-2 text-xs text-text-sub">{STATUS_LABELS[a.status] ?? a.status}</span>
              {a.sequence_no > 1 && <span className="ml-2 text-xs text-text-sub">({a.sequence_no}回目)</span>}
            </p>
          </div>
          {a.work_prohibition === 'conditional_release' && (
            <WorkReleaseSection absence={a} isSelf={isSelf} canManageGrant={canManageGrant} onChanged={onChanged} />
          )}
        </div>
      ))}
    </div>
  );
}

export function LeaveAbsencesPage() {
  const t = useLabel();
  const user = useAuthStore((s) => s.user);
  const canManageAll = hasPermission(user?.effective_permissions, 'leave', 'manage', user?.non_inheriting_features)
    || hasPermission(user?.effective_permissions, 'leave', 'view', user?.non_inheriting_features);
  const canManageGrant = hasTenantWidePermission(user?.effective_permissions, 'leave_grant', 'manage', user?.non_inheriting_features);
  const [employeeId, setEmployeeId] = useState('');

  return (
    <div className="space-y-4">
      <h1 className="text-h1">{t('entity.leave_absences', '長期休業の管理')}</h1>
      <p className="text-sm text-text-sub">産前産後休業・育児休業・出生時育児休業・介護休業の期間を計算し、確定・分割・延長を管理します。</p>

      {canManageAll && (
        <div className="border border-surface-border rounded-lg bg-white p-4 sm:p-5">
          <label className="block text-xs text-text-sub mb-1">対象社員 (指定しなければ自分自身)</label>
          <EmployeeSelector employeeId={employeeId} onChange={setEmployeeId} />
        </div>
      )}

      <ComputeAndConfirmPanel employeeId={employeeId} onConfirmed={() => { /* AbsenceList は react-query 経由で自動更新 */ }} />

      <section className="space-y-2">
        <h2 className="text-h2">休業記録一覧</h2>
        <AbsenceList employeeId={employeeId} isSelf={!employeeId} canManageGrant={canManageGrant} />
      </section>
    </div>
  );
}
